/* fs-search.js - Search Experience controller (Sept 2026 handoff).
   One controller, three surfaces: mobile full-screen sheet, desktop panel under
   the header, results-page chrome. Inert unless html.fss-on (mode 'on' server
   side, or the ab-nav-search-treatment bucket in mode 'ab').
   No dependencies. Talks to /search/suggest.json, /products/{handle}.js,
   /cart/add.js and (optionally) Klaviyo's client subscribe API. */
(function () {
  'use strict';

  var cfgEl = document.getElementById('FssConfig');
  var root = document.getElementById('FsSearch');
  if (!cfgEl || !root) return;

  var CFG;
  try { CFG = JSON.parse(cfgEl.textContent); } catch (e) { return; }

  var doc = document;
  var html = doc.documentElement;
  var DESKTOP = window.matchMedia('(min-width: 990px)');
  var LS_RECENTS = 'fss:recents';
  var LS_AB = 'fss:ab:last';
  var SS_PREVIEW = 'fss_ab_preview';
  var DEBOUNCE_MS = 150;
  var SLOW_MS = 2000;

  /* ---------------- activation (ab plumbing) ---------------- */

  function store(fn) { try { return fn(); } catch (e) { return null; } }

  function setActive(on) {
    var was = html.classList.contains('fss-on');
    html.classList.toggle('fss-on', on);
    if (was !== on) {
      /* the header re-slots; the theme's sticky-wrap height was measured
         before the class landed, so ask it to re-measure */
      window.dispatchEvent(new Event('resize'));
    }
  }

  function isActive() { return html.classList.contains('fss-on'); }

  (function activate() {
    if (CFG.mode === 'on') { setActive(true); return; }
    if (CFG.mode !== 'ab') return;

    /* QA override: ?ab_navsearch=treatment|control|off (theme-side; the app's
       fs_ab_qa param only covers template tests) */
    var m = location.search.match(/[?&]ab_navsearch=(treatment|control|off)/);
    if (m) {
      if (m[1] === 'off') store(function () { sessionStorage.removeItem(SS_PREVIEW); });
      else store(function () { sessionStorage.setItem(SS_PREVIEW, m[1]); });
    }
    var preview = store(function () { return sessionStorage.getItem(SS_PREVIEW); });
    if (preview) { setActive(preview === 'treatment'); return; }

    /* live reconciliation with the FS-AB tracker's body class */
    var apply = function () {
      var cl = doc.body.classList;
      if (cl.contains(CFG.abClassTreatment)) {
        setActive(true);
        store(function () { localStorage.setItem(LS_AB, 'treatment'); });
        return true;
      }
      if (cl.contains(CFG.abClassControl)) {
        setActive(false);
        store(function () { localStorage.setItem(LS_AB, 'control'); });
        return true;
      }
      return false;
    };
    if (!apply()) {
      new MutationObserver(function () { apply(); })
        .observe(doc.body, { attributes: true, attributeFilter: ['class'] });
    }
  })();

  /* ---------------- els ---------------- */

  var shell = root.querySelector('.fss-shell');
  var scrollEl = root.querySelector('[data-fss-scroll]');
  var browseView = root.querySelector('[data-fss-view="browse"]');
  var resultsView = root.querySelector('[data-fss-view="results"]');
  var resultsBox = root.querySelector('#FssListbox');
  var foot = root.querySelector('[data-fss-foot]');
  var seeAll = root.querySelector('[data-fss-seeall]');
  var liveEl = root.querySelector('[data-fss-live]');
  var recentsBlock = root.querySelector('[data-fss-recents]');
  var recentsList = root.querySelector('[data-fss-recents-list]');
  var colorsBlock = root.querySelector('[data-fss-colors-block]');
  var colorsList = root.querySelector('[data-fss-colors]');
  var catsEl = root.querySelector('[data-fss-cats]');
  var helpEl = root.querySelector('[data-fss-help]');

  var state = {
    open: false,
    q: '',
    trigger: null,
    reqSeq: 0,
    aborter: null,
    slowTimer: null,
    activeOpt: -1,
    lastRendered: ''
  };

  /* ---------------- utils ---------------- */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(v) {
    var n = typeof v === 'number' ? v : parseFloat(v);
    if (isNaN(n)) return '';
    var fmt = (CFG.money || '${{amount}}').replace(/<[^>]*>/g, '');
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/, n.toFixed(2));
  }
  function imgUrl(u, w) {
    if (!u) return '';
    return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }
  function splitTitle(t) {
    var m = /^(.*?)\s*\((.+)\)\s*$/.exec(t || '');
    if (m) return { name: m[1].toLowerCase(), desc: m[2].toLowerCase() };
    return { name: String(t || '').toLowerCase(), desc: '' };
  }
  function boldMatch(text, q) {
    var t = String(text), lq = q.toLowerCase(), i = t.toLowerCase().indexOf(lq);
    if (i < 0 || !q) return '<span>' + esc(t) + '</span>';
    return '<span class="fss-dimtext">' + esc(t.slice(0, i)) + '</span><strong>' +
      esc(t.slice(i, i + q.length)) + '</strong><span class="fss-dimtext">' + esc(t.slice(i + q.length)) + '</span>';
  }
  function lev(a, b) {
    if (Math.abs(a.length - b.length) > 2) return 9;
    var m = [], i, j;
    for (i = 0; i <= b.length; i++) m[i] = [i];
    for (j = 0; j <= a.length; j++) m[0][j] = j;
    for (i = 1; i <= b.length; i++) for (j = 1; j <= a.length; j++)
      m[i][j] = Math.min(m[i - 1][j] + 1, m[i][j - 1] + 1, m[i - 1][j - 1] + (b[i - 1] === a[j - 1] ? 0 : 1));
    return m[b.length][a.length];
  }
  function announce(msg) { if (liveEl) liveEl.textContent = msg; }

  function searchHref(q) {
    return CFG.searchUrl + '?q=' + encodeURIComponent(q) +
      '&type=' + encodeURIComponent(CFG.searchType || 'product') + '&options%5Bprefix%5D=last';
  }

  /* ---------------- recents ---------------- */

  function getRecents() {
    var v = store(function () { return JSON.parse(localStorage.getItem(LS_RECENTS) || '[]'); });
    return Array.isArray(v) ? v : [];
  }
  function pushRecent(q) {
    q = (q || '').trim().toLowerCase();
    if (!q) return;
    var r = getRecents().filter(function (x) { return x !== q; });
    r.unshift(q);
    store(function () { localStorage.setItem(LS_RECENTS, JSON.stringify(r.slice(0, 6))); });
  }
  function renderRecents() {
    if (!recentsBlock) return;
    var r = getRecents();
    recentsBlock.hidden = r.length === 0;
    if (!r.length) return;
    recentsList.innerHTML = r.map(function (t) {
      return '<button type="button" class="fss-chip" data-fss-term="' + esc(t) + '">' +
        '<svg class="fss-chip__glyph" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 10a8 8 0 0 1 14-4l3 3M20 14a8 8 0 0 1-14 4l-3-3" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M21 4v5h-5M3 20v-5h5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>' +
        esc(t) + '</button>';
    }).join('');
  }

  /* ---------------- browse extras ---------------- */

  function renderColors() {
    var cols = CFG.colors || [];
    if (!colorsBlock || !cols.length) return;
    colorsBlock.hidden = false;
    colorsList.innerHTML = cols.map(function (c) {
      var href = c.url || searchHref(c.n);
      return '<a class="fss-dot" href="' + esc(href) + '">' +
        '<span class="fss-dot__swatch" style="background:' + esc(c.c) + '"></span>' +
        '<span class="fss-dot__name">' + esc(c.n) + '</span></a>';
    }).join('');
  }

  /* ---------------- placeholder rotation ---------------- */

  var phTimer = null, phIdx = 0;
  function allInputs() { return doc.querySelectorAll('.fss-field__input'); }
  function rotatePlaceholder() {
    var terms = (CFG.placeholders || []).map(function (t) { return String(t).trim(); }).filter(Boolean);
    if (!terms.length) return;
    phIdx = (phIdx + 1) % terms.length;
    allInputs().forEach(function (inp) {
      if (!inp.value) inp.placeholder = 'search ' + terms[phIdx] + '…';
    });
  }
  function startRotation() { stopRotation(); rotatePlaceholder(); phTimer = setInterval(rotatePlaceholder, 3000); }
  function stopRotation() { if (phTimer) { clearInterval(phTimer); phTimer = null; } }

  /* ---------------- open / close ---------------- */

  function activeInput() {
    return root.querySelector('.fss-head .fss-field__input');
  }

  function positionPanel() {
    if (!DESKTOP.matches) { html.style.removeProperty('--fss-top'); return; }
    var header = doc.getElementById('SiteHeader');
    var bottom = 0;
    if (header) {
      var r = header.getBoundingClientRect();
      bottom = Math.max(0, r.bottom);
    }
    html.style.setProperty('--fss-top', bottom + 'px');
  }

  function open(trigger) {
    if (state.open || !isActive()) return;
    state.open = true;
    state.trigger = trigger || null;
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    shell.classList.add('is-open');
    positionPanel();
    if (!DESKTOP.matches) html.classList.add('fss-lock');
    var inp = activeInput();
    var headerField = inp && inp.closest('.fss-field');
    if (headerField) headerField.classList.add('is-open');
    renderRecents();
    renderColors();
    if (inp) {
      if (!inp.value && trigger && trigger.tagName === 'INPUT' && trigger.value) {
        inp.value = trigger.value;
        var cl = inp.closest('.fss-field').querySelector('.fss-field__clear');
        if (cl) cl.hidden = false;
      }
      inp.setAttribute('aria-expanded', 'true');
      if (inp !== doc.activeElement) inp.focus({ preventScroll: true });
      if (inp.value.trim()) { onQuery(inp.value.trim()); } else { showBrowse(); }
    }
    startRotation();
    window.addEventListener('scroll', positionPanel, { passive: true });
    window.addEventListener('resize', positionPanel);
  }

  function close(returnFocus) {
    if (!state.open) return;
    state.open = false;
    root.hidden = true;
    root.setAttribute('aria-hidden', 'true');
    shell.classList.remove('is-open');
    html.classList.remove('fss-lock');
    stopRotation();
    if (state.aborter) state.aborter.abort();
    clearTimeout(state.slowTimer);
    doc.querySelectorAll('.fss-field.is-open').forEach(function (f) { f.classList.remove('is-open'); });
    allInputs().forEach(function (i) { i.setAttribute('aria-expanded', 'false'); });
    window.removeEventListener('scroll', positionPanel);
    window.removeEventListener('resize', positionPanel);
    if (returnFocus !== false && state.trigger && state.trigger.focus) state.trigger.focus();
    state.trigger = null;
  }

  function showBrowse() {
    browseView.hidden = false;
    resultsView.hidden = true;
    foot.hidden = true;
    state.activeOpt = -1;
  }
  function showResults() {
    browseView.hidden = true;
    resultsView.hidden = false;
  }

  /* ---------------- local dictionary ---------------- */

  function dictionary() {
    if (state._dict) return state._dict;
    var d = [];
    (CFG.trending || []).forEach(function (t) { d.push({ t: t, kind: 'term' }); });
    (CFG.fabrics || []).forEach(function (t) { d.push({ t: t, kind: 'term' }); });
    (CFG.colors || []).forEach(function (c) { d.push({ t: c.n, kind: 'color', swatch: c.c, url: c.url }); });
    Object.keys(CFG.families || {}).forEach(function (f) { d.push({ t: f, kind: 'family' }); });
    Object.keys(CFG.synonyms || {}).forEach(function (s) { d.push({ t: s, kind: 'synonym' }); });
    if (helpEl) helpEl.querySelectorAll('[data-fss-help-label]').forEach(function (a) {
      d.push({ t: a.getAttribute('data-fss-help-label').toLowerCase(), kind: 'help', url: a.getAttribute('href') });
    });
    var seen = {};
    state._dict = d.filter(function (e) {
      e.t = String(e.t).trim().toLowerCase();
      if (!e.t || seen[e.t]) return false;
      seen[e.t] = 1; return true;
    });
    return state._dict;
  }
  function localSuggestions(q) {
    var lq = q.toLowerCase();
    var starts = [], contains = [];
    dictionary().forEach(function (e) {
      if (e.t === lq) return;
      if (e.t.indexOf(lq) === 0) starts.push(e);
      else if (e.t.indexOf(lq) > 0) contains.push(e);
    });
    return starts.concat(contains).slice(0, 4);
  }
  function didYouMean(q) {
    var lq = q.toLowerCase(), best = null, bestD = 3;
    dictionary().forEach(function (e) {
      var d = lev(lq, e.t);
      if (d < bestD) { bestD = d; best = e.t; }
    });
    return bestD > 0 && bestD <= 2 ? best : null;
  }

  /* ---------------- network ---------------- */

  function suggest(q, signal) {
    var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product,collection,page,query' +
      '&resources[limit]=8&resources[limit_scope]=each' +
      '&resources[options][unavailable_products]=hide' +
      '&resources[options][fields]=title,product_type,variants.title,vendor,tag,variants.sku';
    return fetch(url, { signal: signal, headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); })
      .then(function (j) { return (j.resources && j.resources.results) || {}; });
  }

  /* ---------------- render helpers ---------------- */

  function tileHTML(p, opts) {
    opts = opts || {};
    var t = splitTitle(p.title);
    var soldout = p.available === false;
    var price = parseFloat(p.price);
    var compare = parseFloat(p.compare_at_price_max || p.compare_at_price || 0);
    var onSale = compare > price;
    var badge = soldout ? 'sold out' : (onSale ? 'sale' : '');
    return '<a class="fss-tile' + (soldout ? ' is-soldout' : '') + '" href="' + esc(p.url) + '" data-fss-nav role="option">' +
      '<span class="fss-tile__media">' +
      (p.image ? '<img src="' + esc(imgUrl(p.image, 360)) + '" alt="' + esc(t.name) + '" loading="lazy">' : '') +
      (badge ? '<span class="fss-tile__badge' + (onSale && !soldout ? ' fss-tile__badge--sale' : '') + '">' + badge + '</span>' : '') +
      '</span>' +
      '<span class="fss-tile__name"><span>' + esc(t.name) + '</span></span>' +
      (t.desc ? '<span class="fss-tile__desc">' + esc(t.desc) + '</span>' : '') +
      '<span class="fss-tile__price' + (onSale ? ' is-sale' : '') + '">' + money(price) +
      (onSale ? ' <span class="fss-compare">' + money(compare) + '</span>' : '') + '</span>' +
      '</a>';
  }

  function sugRow(html_, meta, term, extra) {
    return '<button type="button" class="fss-sug" role="option" data-fss-opt ' + (extra || '') +
      (term ? ' data-fss-term="' + esc(term) + '"' : '') + '>' +
      '<svg class="fss-sug__glyph" viewBox="0 0 64 64" aria-hidden="true"><path d="M47.16 28.58A18.58 18.58 0 1 1 28.58 10a18.58 18.58 0 0 1 18.58 18.58zM54 54L41.94 42" fill="none" stroke="currentColor" stroke-width="5"/></svg>' +
      '<span class="fss-sug__text">' + html_ + '</span>' +
      (meta ? '<span class="fss-sug__meta">' + esc(meta) + '</span>' : '') +
      '</button>';
  }
  function colorSugRow(entry, q) {
    return '<button type="button" class="fss-sug" role="option" data-fss-opt data-fss-term="' + esc(entry.t) + '">' +
      '<span class="fss-sug__swatch" style="background:' + esc(entry.swatch) + '"></span>' +
      '<span class="fss-sug__text">' + boldMatch(entry.t, q) + '</span>' +
      '<span class="fss-sug__meta">color</span></button>';
  }
  function skeletons(n) {
    var s = '';
    for (var i = 0; i < n; i++) s += '<div class="fss-skel"><div class="fss-skel__img"></div><div class="fss-skel__l1"></div><div class="fss-skel__l2"></div></div>';
    return s;
  }
  function group(label, inner, cls) {
    return '<div class="fss-group ' + (cls || '') + '">' +
      (label ? '<div class="fss-group__label">' + esc(label) + '</div>' : '') + inner + '</div>';
  }

  /* ---------------- query pipeline ---------------- */

  var debTimer = null;
  function onQuery(q) {
    state.q = q;
    if (!q) {
      /* cancel anything pending or a late response repaints stale results */
      clearTimeout(debTimer);
      if (state.aborter) state.aborter.abort();
      state.lastRendered = '';
      showBrowse();
      updateFoot('');
      return;
    }
    showResults();
    renderInterim(q);
    clearTimeout(debTimer);
    if (q.length >= 2) {
      debTimer = setTimeout(function () { runNetwork(q); }, DEBOUNCE_MS);
    }
    updateFoot(q);
  }

  function renderInterim(q) {
    /* local suggestions immediately; keep previous products (nothing clears
       between keystrokes) or show skeletons if there is nothing yet */
    var locals = localSuggestions(q).map(function (e) {
      return e.kind === 'color' && e.swatch ? colorSugRow(e, q) : sugRow(boldMatch(e.t, q), e.kind === 'help' ? 'help' : '', e.t);
    }).join('');
    var sugHtml = sugRow('<strong>' + esc(q) + '</strong>', '', q) + locals;
    var prevGrid = resultsBox.querySelector('[data-fss-prodgrid]');
    var prodHtml = prevGrid && state.lastRendered && q.indexOf(state.lastRendered.slice(0, 2)) === 0
      ? prevGrid.outerHTML
      : '<div class="fss-skelrow" data-fss-prodgrid>' + skeletons(DESKTOP.matches ? 4 : 2) + '</div>';
    paintResults({ sug: group('suggestions', sugHtml), main: prodHtml });
    state.activeOpt = -1;
  }

  function runNetwork(q) {
    if (state.aborter) state.aborter.abort();
    var seq = ++state.reqSeq;
    var ctrl = new AbortController();
    state.aborter = ctrl;
    clearTimeout(state.slowTimer);
    state.slowTimer = setTimeout(function () {
      var sl = resultsBox.querySelector('[data-fss-slow]');
      if (sl) sl.hidden = false;
    }, SLOW_MS);

    if (navigator.onLine === false) { renderOffline(q); return; }

    var syn = (CFG.synonyms || {})[q.toLowerCase()];
    var fetches;
    if (syn && syn.length) {
      fetches = Promise.all(syn.slice(0, 3).map(function (c) { return suggest(c, ctrl.signal); }))
        .then(function (all) { return { synonym: syn, merged: mergeResults(all) }; });
    } else {
      fetches = suggest(q, ctrl.signal).then(function (res) { return { res: res }; });
    }

    fetches.then(function (out) {
      if (seq !== state.reqSeq) return;
      clearTimeout(state.slowTimer);
      if (out.synonym) renderSynonym(q, out.synonym, out.merged);
      else renderNetwork(q, out.res);
    }).catch(function (err) {
      if (err && err.name === 'AbortError') return;
      if (seq !== state.reqSeq) return;
      clearTimeout(state.slowTimer);
      renderError(q);
    });
  }

  function mergeResults(all) {
    var products = [], seen = {};
    all.forEach(function (r) {
      (r.products || []).forEach(function (p) {
        if (!seen[p.handle]) { seen[p.handle] = 1; products.push(p); }
      });
    });
    return products.slice(0, 8);
  }

  function dedupeTitles(products) {
    var seen = {}, out = [];
    products.forEach(function (p) {
      var key = splitTitle(p.title).name;
      if (seen[key]) return;
      seen[key] = 1; out.push(p);
    });
    return out;
  }

  function familyFor(q) {
    var fams = CFG.families || {};
    var lq = q.toLowerCase().trim();
    if (fams[lq]) return { key: lq, def: fams[lq] };
    return null;
  }

  function paintResults(parts) {
    /* mobile: flat priority order. desktop: rail (sug/coll/help) + main. */
    var railBits = [parts.sug || '', parts.coll || '', parts.help || ''].join('');
    var mainBits = [parts.notice || '', parts.family || '', parts.mainLabel || '', parts.main || '', parts.slow || ''].join('');
    if (DESKTOP.matches) {
      resultsBox.innerHTML = '<div class="fss-rail">' + railBits + '</div><div class="fss-main">' + mainBits + '</div>';
    } else {
      resultsBox.innerHTML = [parts.notice || '', parts.sug || '', parts.family || '', parts.mainLabel || '', parts.main || '', parts.slow || '', parts.coll || '', parts.help || ''].join('');
    }
  }

  function renderNetwork(q, res) {
    var products = dedupeTitles(res.products || []);
    var collections = res.collections || [];
    var pages = res.pages || [];
    var queries = res.queries || [];
    var fam = familyFor(q);

    if (!products.length && !collections.length && !pages.length && !fam) {
      var coming = (CFG.comingSoon || []).find(function (c) {
        return c.name && c.name.toLowerCase() === q.toLowerCase();
      });
      if (coming) return renderComing(q, coming);
      return renderNoMatches(q, queries);
    }

    var parts = {};

    /* suggestions: api queries first, then local dictionary fills */
    var sugRows = [];
    queries.slice(0, 4).forEach(function (s) {
      sugRows.push(sugRow(boldMatch(s.text, q), '', s.text));
    });
    if (!sugRows.length) sugRows.push(sugRow('<strong>' + esc(q) + '</strong>', '', q));
    localSuggestions(q).slice(0, 4 - Math.min(sugRows.length, 3)).forEach(function (e) {
      sugRows.push(e.kind === 'color' && e.swatch ? colorSugRow(e, q) : sugRow(boldMatch(e.t, q), '', e.t));
    });
    parts.sug = group('suggestions', sugRows.join(''));

    /* help intent leads when the query smells like a policy question */
    var helpHits = pages.slice(0, 3).map(function (p) {
      return '<a class="fss-sug" role="option" data-fss-opt href="' + esc(p.url) + '">' +
        '<svg class="fss-sug__glyph" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 3h7l4 4v14H7z" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>' +
        '<span class="fss-sug__text">' + boldMatch(p.title.toLowerCase(), q) + '</span></a>';
    });
    var isHelpIntent = (CFG.helpWords || []).some(function (w) { return q.toLowerCase().indexOf(w) === 0; });
    if (helpHits.length) {
      parts.help = group('help & info', helpHits.join(''));
      if (isHelpIntent) { parts.sug = parts.help + parts.sug; parts.help = ''; }
    }

    /* family card */
    if (fam) {
      parts.family = familyCard(fam, res.products || []);
      var chipHandles = {};
      (fam.def.chips || []).forEach(function (c) { chipHandles[c.handle] = 1; });
      chipHandles[fam.def.primary] = 1;
      /* "more from the family" must actually be family: the API also matches
         unrelated styles (johnny, saylor) on body copy for family queries */
      var famKey = String(fam.key).toLowerCase();
      products = products.filter(function (p) {
        return !chipHandles[p.handle] && String(p.title).toLowerCase().indexOf(famKey) > -1;
      });
      parts.mainLabel = products.length ? '<div class="fss-group__label">more from the family</div>' : '';
    } else if (products.length) {
      parts.mainLabel = '<div class="fss-group__label">products</div>';
    }

    if (products.length) {
      parts.main = '<div class="fss-grid" data-fss-prodgrid>' +
        products.slice(0, DESKTOP.matches ? 8 : 6).map(function (p) { return tileHTML(p); }).join('') + '</div>';
    } else if (!fam) {
      parts.main = '<div class="fss-note">no products match "' + esc(q) + '"</div>';
    }
    parts.slow = '<div class="fss-slow" data-fss-slow hidden>still looking…</div>';

    /* collections & colors — one row per label: the API's "white" collection
       and the local "white" color dictionary entry must not both render */
    var collSeen = {};
    var collBits = [];
    collections.slice(0, 3).forEach(function (c) {
      var key = String(c.title).toLowerCase().trim();
      if (collSeen[key]) return;
      collSeen[key] = 1;
      collBits.push('<a class="fss-coll" href="' + esc(c.url) + '">' +
        '<span class="fss-coll__img"' + (c.featured_image && c.featured_image.url ? ' style="background-image:url(' + esc(imgUrl(c.featured_image.url, 80)) + ')"' : '') + '></span>' +
        esc(key) + '</a>');
    });
    (CFG.colors || []).forEach(function (c) {
      if (collBits.length >= 4) return;
      var key = c.n.toLowerCase().trim();
      if (collSeen[key]) return;
      if (key.indexOf(q.toLowerCase()) > -1) {
        collSeen[key] = 1;
        collBits.push('<a class="fss-coll" href="' + esc(c.url || searchHref(c.n)) + '">' +
          '<span class="fss-coll__img" style="background:' + esc(c.c) + '"></span>' + esc(c.n) + '</a>');
      }
    });
    if (collBits.length) parts.coll = group('collections & colors', '<div class="fss-collrow">' + collBits.join('') + '</div>');

    paintResults(parts);
    state.lastRendered = q;
    state.activeOpt = -1;
    announce(products.length ? products.length + ' results for ' + q : 'suggestions updated');
    updateFoot(q);
  }

  function familyCard(fam, products) {
    var d = fam.def;
    /* image + presentment-correct price come from the suggest payload itself:
       /products/{handle}.js is shop-currency and would mislabel intl sessions */
    var primary = (products || []).filter(function (p) { return p.handle === d.primary; })[0];
    /* the primary style's standard price (price_max): a "from <min of every
       ziggy-anything>" reads as the ziggy's price and markdown collab
       colours/minis drag it far below the core $ */
    var famPrice = primary ? parseFloat(primary.price_max || primary.price) : NaN;
    var chips = (d.chips || []).map(function (c) {
      return '<a class="fss-family__chip" href="/products/' + esc(c.handle) + '">' + esc(c.label) + '</a>';
    }).join('');
    var img = primary && primary.image ? ' style="background-image:url(' + esc(imgUrl(primary.image, 340)) + ')"' : '';
    /* chips are anchors, so they must not nest inside the card link — the
       parser would hoist them out of the card (anchors cannot contain anchors) */
    var card = '<div class="fss-family" data-fss-family="' + esc(fam.key) + '">' +
      '<a class="fss-family__link" href="' + esc(d.url || '/products/' + d.primary) + '">' +
      '<span class="fss-family__img"' + img + '></span>' +
      '<span><span class="fss-family__name">' + esc(fam.key) + '</span>' +
      '<span class="fss-family__desc">' + esc(d.desc || '') + '</span>' +
      (!isNaN(famPrice) ? '<span class="fss-family__price">' + money(famPrice) + '</span>' : '') +
      '</span></a>' +
      '<span class="fss-family__chips">' + chips + '</span></div>';
    return group('the ' + fam.key + ' family', card);
  }

  function renderSynonym(q, syn, products) {
    var parts = {};
    var rows = syn.map(function (name) {
      var conf = (CFG.colors || []).find(function (c) { return c.n.toLowerCase() === name.toLowerCase(); });
      return conf ? colorSugRow({ t: conf.n, swatch: conf.c }, q) : sugRow('<strong>' + esc(name) + '</strong>', 'color', name);
    });
    parts.sug = group('suggestions', rows.join(''));
    var label = syn.slice(0, 2).join(' & ');
    if (products.length) {
      parts.mainLabel = '<div class="fss-group__label">products in ' + esc(label) + '</div>';
      parts.main = '<div class="fss-grid" data-fss-prodgrid>' +
        products.slice(0, DESKTOP.matches ? 8 : 4).map(function (p) { return tileHTML(p); }).join('') + '</div>';
    } else {
      parts.main = '<div class="fss-note">nothing in ' + esc(label) + ' right now</div>';
    }
    paintResults(parts);
    state.lastRendered = q;
    announce(products.length + ' results in ' + label);
    updateFoot(syn[0]);
  }

  function renderNoMatches(q, queries) {
    var dym = (queries && queries[0] && queries[0].text) || didYouMean(q);
    var chips = '';
    if (dym) chips += '<button type="button" class="fss-chip fss-chip--strong" data-fss-term="' + esc(dym) + '">did you mean: <strong>&nbsp;' + esc(dym) + '</strong>?</button>';
    var trendChips = (CFG.trending || []).slice(0, 8).map(function (t) {
      return '<button type="button" class="fss-chip" data-fss-term="' + esc(String(t).trim()) + '">' + esc(String(t).trim()) + '</button>';
    }).join('');
    var cats = catsEl ? '<div class="fss-scrollwrap"><div class="fss-cats">' + catsEl.innerHTML + '</div></div>' : '';
    resultsBox.innerHTML = '<div class="fss-empty">' +
      '<div class="fss-empty__title">no matches for "' + esc(q) + '" yet</div>' +
      (chips ? '<div class="fss-empty__chips">' + chips + '</div>' : '') +
      '<div class="fss-group__label">try one of these</div>' +
      '<div class="fss-empty__chips">' + trendChips + '</div>' +
      (cats ? '<div class="fss-group__label">or browse</div>' + cats : '') +
      '<div class="fss-anyway"><a href="' + esc(searchHref(q)) + '" data-fss-nav>search anyway</a></div>' +
      '</div>';
    state.lastRendered = q;
    announce('no matches for ' + q);
    updateFoot(q);
  }

  function renderComing(q, coming) {
    var capture = '';
    if (CFG.klaviyo && CFG.klaviyo.company && CFG.klaviyo.list) {
      capture = '<form class="fss-coming__row" data-fss-coming-form data-name="' + esc(coming.name) + '">' +
        '<input class="fss-coming__email" type="email" required placeholder="your email" aria-label="your email">' +
        '<button class="fss-coming__btn" type="submit">notify me</button></form>';
    }
    resultsBox.innerHTML = '<div class="fss-coming">' +
      '<div class="fss-coming__eyebrow">coming soon</div>' +
      '<div class="fss-coming__title">' + esc(coming.name) + ' is landing soon</div>' +
      '<div class="fss-coming__blurb">' + esc(coming.blurb || 'a new style joins the family soon - be first to know.') + '</div>' +
      capture + '</div>';
    state.lastRendered = q;
    announce(coming.name + ' is coming soon');
    updateFoot(q);
  }

  function renderError(q) {
    var notice = '<div class="fss-error"><span class="fss-error__text">search is having a moment. try again</span>' +
      '<button type="button" class="fss-error__retry" data-fss-retry="' + esc(q) + '">retry</button></div>';
    var existing = resultsBox.querySelector('.fss-error');
    if (existing) return;
    resultsBox.insertAdjacentHTML('afterbegin', notice);
  }

  function renderOffline(q) {
    resultsBox.innerHTML = '<div class="fss-offline">' +
      '<svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true"><path d="M2 8c6-5 14-5 20 0M5 12c4-3.5 10-3.5 14 0M8.5 15.8c2-1.8 5-1.8 7 0M12 19.5h.01" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M3 3l18 18" stroke="currentColor" stroke-width="1.6"/></svg>' +
      "you're offline - recents and browsing still work</div>";
    showBrowse();
    resultsView.hidden = false;
  }

  function updateFoot(q) {
    if (!q) { foot.hidden = true; return; }
    foot.hidden = false;
    seeAll.textContent = 'see all results for "' + q + '"';
    seeAll.href = searchHref(q);
  }

  /* ---------------- keyboard / combobox ---------------- */

  function options() { return Array.prototype.slice.call(resultsBox.querySelectorAll('[data-fss-opt]')); }
  function setActiveOpt(idx) {
    var opts = options();
    opts.forEach(function (o, i) {
      o.classList.toggle('is-active', i === idx);
      if (i === idx) { o.id = o.id || 'fss-opt-' + i; }
    });
    state.activeOpt = idx;
    var inp = activeInput();
    if (inp) {
      if (idx > -1 && opts[idx]) inp.setAttribute('aria-activedescendant', opts[idx].id);
      else inp.removeAttribute('aria-activedescendant');
    }
    if (idx > -1 && opts[idx]) opts[idx].scrollIntoView({ block: 'nearest' });
  }

  function onKeydown(e) {
    if (!state.open) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    var opts = options();
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!opts.length) return;
      e.preventDefault();
      var next = e.key === 'ArrowDown' ? state.activeOpt + 1 : state.activeOpt - 1;
      if (next >= opts.length) next = 0;
      if (next < -1) next = opts.length - 1;
      setActiveOpt(next);
      return;
    }
    if (e.key === 'Enter') {
      var inp = activeInput();
      if (state.activeOpt > -1 && opts[state.activeOpt]) {
        e.preventDefault(); opts[state.activeOpt].click(); return;
      }
      if (inp && inp.value.trim()) {
        e.preventDefault();
        pushRecent(inp.value.trim());
        window.location.href = searchHref(inp.value.trim());
      }
    }
  }

  /* ---------------- events ---------------- */

  function bindField(inp) {
    if (!inp || inp._fss) return;
    inp._fss = true;
    inp.addEventListener('input', function () {
      var field = inp.closest('.fss-field');
      var clear = field && field.querySelector('.fss-field__clear');
      if (clear) clear.hidden = !inp.value;
      if (!state.open) open(inp);
      onQuery(inp.value.trim());
    });
    inp.addEventListener('focus', function () {
      if (isActive() && !state.open) open(inp);
    });
    var field = inp.closest('.fss-field');
    var clear = field && field.querySelector('.fss-field__clear');
    if (clear) clear.addEventListener('click', function () {
      inp.value = ''; clear.hidden = true; inp.focus(); onQuery('');
    });
  }

  function init() {
    allInputs().forEach(bindField);

    doc.addEventListener('keydown', onKeydown, true);

    /* `/` focuses search (desktop), like the spec's kbd hint */
    doc.addEventListener('keydown', function (e) {
      if (e.key !== '/' || state.open || !isActive()) return;
      var t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      e.preventDefault();
      open(doc.activeElement);
    });

    /* legacy trigger interception */
    doc.addEventListener('click', function (e) {
      if (!isActive()) return;
      var t = e.target.closest('.js-search-header');
      if (t) { e.preventDefault(); e.stopPropagation(); open(t); return; }
      var term = e.target.closest('[data-fss-term]');
      if (term && root.contains(term)) {
        e.preventDefault();
        var q = term.getAttribute('data-fss-term');
        var inp = activeInput();
        if (inp) { inp.value = q; }
        pushRecent(q);
        onQuery(q);
        return;
      }
      var retry = e.target.closest('[data-fss-retry]');
      if (retry && root.contains(retry)) { runNetwork(retry.getAttribute('data-fss-retry')); return; }
      var nav = e.target.closest('[data-fss-nav]');
      if (nav && root.contains(nav) && state.q) pushRecent(state.q);
      if (e.target.closest('[data-fss-close]')) { e.preventDefault(); close(); }
      var clearR = e.target.closest('[data-fss-clear-recents]');
      if (clearR) {
        store(function () { localStorage.removeItem(LS_RECENTS); });
        renderRecents();
      }
    }, true);

    /* drawer search input hands off to the sheet */
    doc.addEventListener('focusin', function (e) {
      if (!isActive()) return;
      if (e.target.closest && e.target.closest('.pwt-drawer__search')) {
        e.target.blur();
        var closeBtn = doc.querySelector('.js-drawer-close, [data-drawer-close], #NavDrawer .drawer__close button');
        if (closeBtn) closeBtn.click();
        setTimeout(function () { open(e.target); }, 60);
        /* the theme restores focus to the drawer trigger on close - re-assert */
        [250, 500].forEach(function (t) {
          setTimeout(function () {
            var inp = activeInput();
            if (state.open && inp && doc.activeElement !== inp) inp.focus({ preventScroll: true });
          }, t);
        });
      }
    });

    /* coming-soon capture */
    doc.addEventListener('submit', function (e) {
      var f = e.target.closest('[data-fss-coming-form]');
      if (!f) return;
      e.preventDefault();
      var email = f.querySelector('input[type="email"]').value.trim();
      if (!email) return;
      var name = f.getAttribute('data-name');
      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(CFG.klaviyo.company), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-02-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email: email, properties: { fss_coming_soon: name } } } }
            },
            relationships: { list: { data: { type: 'list', id: CFG.klaviyo.list } } }
          }
        })
      }).then(function () {
        f.outerHTML = '<div class="fss-coming__ok">we’ll email you when ' + esc(name) + ' lands</div>';
      }).catch(function () {
        f.outerHTML = '<div class="fss-coming__ok">something went wrong - try again in a moment</div>';
      });
    });

    window.addEventListener('online', function () { if (state.open && state.q) runNetwork(state.q); });
    DESKTOP.addEventListener('change', function () {
      if (state.open && state.q) { renderInterim(state.q); runNetwork(state.q); }
    });

    /* element-tracking hook for FS-AB engagement metrics */
    new MutationObserver(function () {
      shell.classList.toggle('is-open', state.open);
    }).observe(root, { attributes: true, attributeFilter: ['hidden'] });

    initResultsPage();
  }

  /* ---------------- results page chrome (s5/s6) ---------------- */

  function initResultsPage() {
    var head = doc.querySelector('.fss-r-head');
    if (!head) return;
    var inp = head.querySelector('.fss-field__input');
    if (inp) bindField(inp);
    var bar = doc.querySelector('.fss-r-bar');
    if (bar) {
      var filterChip = bar.querySelector('[data-fss-r-filter]');
      var legacyFilter = doc.querySelector('.js-drawer-open-collection-filters');
      var sidebar = doc.getElementById('CollectionSidebar') || doc.querySelector('.collection-sidebar, [data-section-type="collection-template"] .grid__item--sidebar');
      if (filterChip && legacyFilter) {
        /* reuse the theme's own filter drawer */
        filterChip.addEventListener('click', function () { legacyFilter.click(); });
      } else if (filterChip && sidebar) {
        filterChip.addEventListener('click', function () {
          sidebar.classList.toggle('fss-r-sheet-open');
          html.classList.toggle('fss-lock', sidebar.classList.contains('fss-r-sheet-open') && !DESKTOP.matches);
        });
      } else if (filterChip) { filterChip.hidden = true; }
      var sort = bar.querySelector('[data-fss-r-sort]');
      if (sort) sort.addEventListener('change', function () {
        var u = new URL(window.location.href);
        u.searchParams.set('sort_by', sort.value);
        window.location.href = u.toString();
      });
    }
  }

  if (isActive()) startRotation();
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init);
  else init();
})();
